const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Any text from a user or the network, before it enters a template string. */
export const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, c => ESC[c]);

/** Safe colour for a `style` attribute: only a hex colour passes, otherwise `fallback`. */
export const safeColor = (c: unknown, fallback = '#ffd23f'): string => (typeof c === 'string' && /^#[0-9a-f]{3,8}$/i.test(c) ? c : fallback);

/** How long an action may stay silent: a quick one never flashes a spinner, a slower one is seen to be working. */
export const BUSY_DELAY_MS = 150;

/** Runs `work` and marks `el` as working while it lasts (`aria-busy`, styled by the app). A rejection still reaches the caller. */
export function busyWhile<R>(el: Element, work: () => Promise<R>): Promise<R> {
  const timer = setTimeout(() => el.setAttribute('aria-busy', 'true'), BUSY_DELAY_MS);
  return work().finally(() => {
    clearTimeout(timer);
    el.removeAttribute('aria-busy');
  });
}

/** The control a player pressed to start an action: the button itself, or the form's submit button. */
function controlOf(e: Event, tg: Element): Element {
  if (e.type !== 'submit') return tg;
  return (e as SubmitEvent).submitter ?? tg.querySelector('[type=submit]') ?? tg;
}

/**
 * Delegated handler: on(root, 'click', '[data-act]', (e, tg) => …). Returns what removes it.
 * A click or submit handler that returns a promise is an action in progress: its control shows it, and a second press
 * is ignored until it ends. Return the promise of anything that takes time, so that the player sees it working.
 */
export function on<E extends Event = any, T extends HTMLElement = any>(root: ParentNode & EventTarget, type: string, sel: string, fn: (e: E, tg: T) => unknown): () => void {
  const inProgress = new WeakSet<Element>();
  const h = (e: Event) => {
    const tg = (e.target as Element | null)?.closest?.<T>(sel);
    if (!tg || !root.contains(tg)) return;
    const pressed = type === 'click' || type === 'submit';
    if (pressed && inProgress.has(tg)) {
      e.preventDefault(); // an ignored submit must not reload the page
      return;
    }
    const result = fn(e as E, tg);
    if (!pressed || !(result instanceof Promise)) return;
    inProgress.add(tg);
    // no catch: a rejection the handler left unhandled is still reported as one
    void busyWhile(controlOf(e, tg), () => result).finally(() => inProgress.delete(tg));
  };
  root.addEventListener(type, h);
  return () => root.removeEventListener(type, h);
}
